import React, { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Dashboard from "../screens/content/dashboard";
import NotFound from "../screens/content/notFound";
import ScrollToTop from "./scrollToTop";
import { isBrowserDefined } from "../shared/services/browserService";
import CustomLayout from "../screens/customLayout";
import "../translation/index";
import { routes } from "../config/routes";
import { Provider } from "react-redux";
import Login from "../screens/auth/login";
import ProtectedRoute from "./protectedRoute";
import PublicRoute from "./publicRoute";
import DoctorsList from "../screens/content/doctors/list";
import { store } from "../redux/store";
import { storage } from "../shared/utils/storage";
import { STORAGE_KEYS } from "../config/keys";
import { setCurrentUser } from "../screens/auth/login/reducer";
import AddNewDoctor from "../screens/content/doctors/addNewDoctor";
import AddPatients from "../screens/content/patients/addPatients";
import PatientsList from "../screens/content/patients/list";
import PatientDetailsPage from "../screens/content/patients/patientDetails";
import MedicationList from "../screens/content/medications/list";
import Loader from "../connected-components/loader";
import Modal from "../connected-components/modal";
import { ToastContainer } from "react-toastify";
import DrugsDiseases from "../screens/content/drugsAndDiseases";
import Congresses from "../screens/content/congresses";
import MedicalCalculators from "../screens/content/congresses/medicalCalculators";
import QAScreen from "../screens/content/qa";
import MedicalUpdates from "../screens/content/congresses/medicalUpdates";
import NewsDetails from "../screens/content/congresses/newsDetails";
import LaboratoryValues from "../screens/content/congresses/laboratoryValues";
import Medora360MD from "../screens/content/genioMD";
import ClinicalCaseScreen from "../screens/content/clinicalCase";
import DrugDiseaseDetails from "../screens/content/drugsAndDiseases/drugDiseaseDetails";
import InvoicesDashboard from "../screens/content/billing/InvoicesDashboard";
import ClaimsDashboard from "../screens/content/billing/ClaimsDashboard";
import CurrenciesDashboard from "../screens/content/settings/currencies";
import InsurersDashboard from "../screens/content/settings/insurers";
import BillingCodes from "../screens/content/billing/BillingCodes";
import PharmacyInventory from "../screens/content/pharmacy/inventory";
import ApproveMedicationRequests from "../screens/content/pharmacy/approveRequests";
import PharmacySuppliersPage from "../screens/content/pharmacy/suppliers";

/**
 * MainRouter component that handles the routing for the application.
 *
 * This component sets up the routes and ensures that the scroll restoration behavior is set to manual
 * when the application is running in a browser environment. It also wraps the routes with a Redux Provider
 * and a custom layout.
 *
 * @returns {JSX.Element} The MainRouter component.
 */
const MainRouter = (): JSX.Element => {
  useEffect(() => {
    if (isBrowserDefined()) {
      window.history.scrollRestoration = "manual";
      //@ts-ignore
      const storedUser = storage.get(STORAGE_KEYS.USER, null);
      if (storedUser) {
        store.dispatch(setCurrentUser(storedUser));
      }
    }
  }, []);

  return (
    <Provider store={store}>
      <CustomLayout>
        {isBrowserDefined() && <ScrollToTop />}
        <Loader />
        <Modal />
        <ToastContainer
          position="bottom-left"
          toastClassName="!border !border-white/20 !rounded-xl"
        />
        <Routes>
          <Route
            path={routes.auth.login}
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path={routes.dashboard.home}
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path={routes.doctors.list}
            element={
              <ProtectedRoute>
                <DoctorsList />
              </ProtectedRoute>
            }
          />
          <Route
            path={routes.doctors.add}
            element={
              <ProtectedRoute>
                <AddNewDoctor />
              </ProtectedRoute>
            }
          />
          <Route
            path={routes.patients.add}
            element={
              <ProtectedRoute>
                <AddPatients />
              </ProtectedRoute>
            }
          />
          <Route
            path={routes.patients.list}
            element={
              <ProtectedRoute>
                <PatientsList />
              </ProtectedRoute>
            }
          />
          <Route
            path={routes.medications.list}
            element={
              <ProtectedRoute>
                <MedicationList />
              </ProtectedRoute>
            }
          />
          <Route
            path={routes.qa.home}
            element={
              <ProtectedRoute>
                <QAScreen />
              </ProtectedRoute>
            }
          />
          <Route
            path={routes.qa.thread(":threadId")}
            element={
              <ProtectedRoute>
                <QAScreen />
              </ProtectedRoute>
            }
          />
          <Route
            path={routes.patients.details(":id")}
            element={
              <ProtectedRoute>
                <PatientDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={routes.medical.drugsAndDiseases}
            element={
              <ProtectedRoute>
                <DrugsDiseases />
              </ProtectedRoute>
            }
          />
          <Route
            path={routes.medical.congresses}
            element={
              <ProtectedRoute>
                <Congresses />
              </ProtectedRoute>
            }
          />
          <Route
            path={routes.medical.calculators}
            element={
              <ProtectedRoute>
                <MedicalCalculators />
              </ProtectedRoute>
            }
          />
          <Route
            path={routes.medical.tab(":id")}
            element={
              <ProtectedRoute>
                <MedicalUpdates />
              </ProtectedRoute>
            }
          />
          <Route
            path={routes.medical.newsDetails(":tabId", ":id")}
            element={
              <ProtectedRoute>
                <NewsDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path={routes.medical.laboratoryValues}
            element={
              <ProtectedRoute>
                <LaboratoryValues />
              </ProtectedRoute>
            }
          />
          <Route
            path={routes.pharmacy.inventory}
            element={
              <ProtectedRoute>
                <PharmacyInventory />
              </ProtectedRoute>
            }
          />
          <Route
            path={routes.pharmacy.approveRequests}
            element={
              <ProtectedRoute>
                <ApproveMedicationRequests />
              </ProtectedRoute>
            }
          />
          <Route
            path={routes.pharmacy.suppliers}
            element={
              <ProtectedRoute>
                <PharmacySuppliersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={routes.Medora360MD.list}
            element={
              <ProtectedRoute>
                <Medora360MD />
              </ProtectedRoute>
            }
          />
          <Route
            path={routes.Medora360MD.clinicalCase(":tabId")}
            element={
              <ProtectedRoute>
                <ClinicalCaseScreen />
              </ProtectedRoute>
            }
          />
          <Route
            path={routes.medical.drugsAndDiseasesDetails(":id", ":tabId")}
            element={
              <ProtectedRoute>
                <DrugDiseaseDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path={routes.billing.invoices}
            element={
              <ProtectedRoute>
                <InvoicesDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path={routes.billing.claims}
            element={
              <ProtectedRoute>
                <ClaimsDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path={routes.billing.codes}
            element={
              <ProtectedRoute>
                <BillingCodes />
              </ProtectedRoute>
            }
          />
          <Route
            path={routes.settings.currencies}
            element={
              <ProtectedRoute>
                <CurrenciesDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path={routes.settings.insurers}
            element={
              <ProtectedRoute>
                <InsurersDashboard />
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<Navigate to={routes.dashboard.home} />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </CustomLayout>
    </Provider>
  );
};

export default MainRouter;
